import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Label, Input } from "reactstrap";
import { FormattedDate } from "react-intl";
import ConvertToMoney from "./convert-to-money";
import computeTotalAmount, { discountAmount } from "./compute-total-amount";

const propTypes = {
  campaignPackage: PropTypes.shape({
    discount_line_amount: PropTypes.number,
    discount_line_label: PropTypes.string,
    display_title: PropTypes.string,
    order_line_amount: PropTypes.number,
    order_line_label: PropTypes.string,
    renewal_date: PropTypes.node,
    total_cost_label: PropTypes.string
  }).isRequired,
  siteCreditAmount: PropTypes.number,
  updateStateCallback: PropTypes.func.isRequired,
  useSiteCredit: PropTypes.bool.isRequired,
  countryIso: PropTypes.string,
  coupons: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      discount_percent: PropTypes.number,
      discount_cents: PropTypes.number
    })
  ),
  verifiedCoupon: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    status: PropTypes.string,
    partner_id: PropTypes.number,
    discount_cents: PropTypes.number,
    discount_percent: PropTypes.number,
    allow_other_coupons: PropTypes.bool
  }),
  hideCouponLine: PropTypes.bool,
  impressionBoost: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    impression_quantity: PropTypes.number,
    cost: PropTypes.number
  })
};

const OrderSummarySection = ({
  campaignPackage,
  siteCreditAmount = 0,
  updateStateCallback,
  useSiteCredit,
  countryIso = "US",
  coupons = [],
  verifiedCoupon = {},
  hideCouponLine = false,
  impressionBoost = null
}) => {
  const [submitVerifiedCoupon, setSubmitVerifiedCoupon] = useState(true);

  const siteCredit = () => {
    return siteCreditAmount && siteCreditAmount > 0;
  };

  const totalAfterDiscount = () => {
    const output = campaignPackage.order_line_amount || 0;
    const discount = campaignPackage.discount_line_amount;
    return discount && discount > 0 ? output - discount : output;
  };

  const impressionBoostCost = () => {
    return impressionBoost && impressionBoost.cost > 0 ? impressionBoost.cost : 0;
  };

  const totalBeforeCoupons = () => {
    return totalAfterDiscount() + impressionBoostCost();
  };

  const formattedBoost = () => {
    return new Intl.NumberFormat("en-US", {
      maximumSignificantDigits: 3
    }).format(impressionBoost.impression_quantity);
  };

  const couponAmount = () => {
    let total = totalBeforeCoupons();
    const totalBeforeCoupon = total;
    if (coupons && coupons.length > 0 && !hideCouponLine) {
      coupons.forEach(coupon => {
        total -= discountAmount(coupon, totalBeforeCoupon);
      });
    }
    if (verifiedCoupon?.code && !hideCouponLine && submitVerifiedCoupon) {
      total -= discountAmount(verifiedCoupon, totalBeforeCoupon);
    }
    return totalBeforeCoupon - total;
  };

  const totalBeforeCredit = () => {
    return totalBeforeCoupons() - couponAmount();
  };

  const toggleUseSiteCredit = event => {
    updateStateCallback({ useSiteCredit: event.target.checked });
  };

  const siteCreditReadyToUse = () => {
    if (siteCredit()) {
      return Math.min(totalBeforeCredit(), siteCreditAmount);
    }
    return 0;
  };

  const appliedSiteCreditAmount = () => {
    if (useSiteCredit) {
      return siteCreditReadyToUse();
    }
    return 0;
  };

  const totalAmount = () => {
    return computeTotalAmount({
      campaignPackage,
      impressionBoost,
      coupons,
      verifiedCoupon,
      siteCreditAmount,
      useSiteCredit,
      hideCouponLine,
      includeVerifiedCoupon: submitVerifiedCoupon
    });
  };

  const removeVerifiedCoupon = () => {
    setSubmitVerifiedCoupon(false);
    updateStateCallback({ coupon: null });
  };

  useEffect(() => {
    if (verifiedCoupon?.code) {
      setSubmitVerifiedCoupon(true);
      updateStateCallback({ coupon: { code: verifiedCoupon.code } });
    }
  }, [verifiedCoupon]);

  return (
    <div>
      <hr />
      <h5 className="font-weight-bold">{campaignPackage.display_title || "Order Summary"}</h5>
      {campaignPackage.order_line_amount && (
        <div className="d-flex justify-content-between text-muted py-1">
          <div
            className="mr-3"
            dangerouslySetInnerHTML={{
              __html: campaignPackage.order_line_label
            }}
          />
          <ConvertToMoney
            number={campaignPackage.order_line_amount}
            countryIso={countryIso}
            className="text-right"
          />
        </div>
      )}
      {campaignPackage.discount_line_amount > 0 ? (
        <div className="d-flex justify-content-between text-muted py-1">
          <div className="mr-3">{campaignPackage.discount_line_label}</div>
          <ConvertToMoney
            number={-campaignPackage.discount_line_amount}
            countryIso={countryIso}
            className="text-right"
          />
        </div>
      ) : (
        ""
      )}
      {impressionBoost && impressionBoost.cost > 0 ? (
        <div className="d-flex justify-content-between text-muted py-1">
          <div className="mr-3">{formattedBoost()} Additional Views</div>
          <ConvertToMoney
            number={impressionBoost.cost}
            countryIso={countryIso}
            className="text-right"
          />
        </div>
      ) : (
        ""
      )}
      {coupons && coupons.length > 0 && !hideCouponLine
        ? coupons.map(coupon => (
            <div
              key={`coupon-${coupon.code}`}
              className="d-flex justify-content-between text-muted py-1"
            >
              <div className="mr-3">
                <span className="text-muted font-italic">Discount Code: {coupon.code}</span>
              </div>
              <ConvertToMoney
                number={-discountAmount(coupon, totalBeforeCoupons())}
                countryIso={countryIso}
                className="text-right text-muted"
              />
            </div>
          ))
        : ""}
      {verifiedCoupon?.code && !hideCouponLine && submitVerifiedCoupon && (
        <div
          key={`coupon-${verifiedCoupon.code}`}
          className="d-flex justify-content-between text-muted py-1"
        >
          <div className="mr-3">
            <span className="text-muted font-italic">Discount Code: {verifiedCoupon.code}</span>
            <button
              className="btn btn-link p-0 pl-1 pb-1"
              type="button"
              data-testid={`remove-coupon-${verifiedCoupon.code}`}
              onClick={() => removeVerifiedCoupon()}
            >
              <span className="fa fa-times icon-medium" />
            </button>
          </div>
          <ConvertToMoney
            number={-discountAmount(verifiedCoupon, totalBeforeCoupons())}
            countryIso={countryIso}
            className="text-right text-muted"
          />
        </div>
      )}
      {siteCredit() ? (
        <div className="d-flex justify-content-between text-muted py-1">
          <div className="align-items-center mr-2 mr-sm-5">
            <Label check className="text-muted pl-4">
              <Input
                type="checkbox"
                defaultChecked={useSiteCredit}
                onClick={toggleUseSiteCredit}
                className="mr-2"
                disabled={totalBeforeCredit() === 0}
              />
              Apply up to{" "}
              <ConvertToMoney
                number={siteCreditReadyToUse()}
                countryIso={countryIso}
                className="d-inline p-0 m-0"
              />{" "}
              from my account credit
            </Label>
          </div>
          <ConvertToMoney
            number={-appliedSiteCreditAmount()}
            countryIso={countryIso}
            className="text-right"
          />
        </div>
      ) : (
        ""
      )}

      <div className="d-flex justify-content-between font-weight-bold py-1">
        <div className="mr-3">{campaignPackage.total_cost_label || "Today's Total"}</div>
        <ConvertToMoney number={totalAmount()} countryIso={countryIso} className="text-right" />
      </div>
      {campaignPackage.renewal_date && (
        <p className="font-italic font-size-small">
          Your next renewal date will be&nbsp;
          <FormattedDate
            value={campaignPackage.renewal_date}
            day="numeric"
            month="long"
            year="numeric"
          />
        </p>
      )}
    </div>
  );
};

OrderSummarySection.propTypes = propTypes;

export default OrderSummarySection;
